import { jsPDF } from 'jspdf';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell } from 'docx';
import {
  attachDocumentByTenant,
  createDocumentByTenant,
  createTenantFile,
} from '../../services/index.js';
import {
  extractPlainTextFromHtml,
  htmlToDocxSectionChildren,
  renderHtmlToPdf,
} from './chatDocumentDownloadUtils.js';

/** Tenant create + file create can exceed the default API client timeout (10s). */
const DRIVE_SAVE_API_TIMEOUT_MS = 120_000;

/** @type {{ value: string, label: string, mimeType: string }[]} */
export const SAVE_DRIVE_FILE_TYPES = [
  { value: 'txt', label: 'Text (.txt)', mimeType: 'text/plain' },
  { value: 'pdf', label: 'PDF (.pdf)', mimeType: 'application/pdf' },
  {
    value: 'docx',
    label: 'Word Document (.docx)',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  },
];

function isHtmlContent(content, contentFormat) {
  return (
    contentFormat === 'html' ||
    (contentFormat === 'auto' && /<\/?[a-z][\s\S]*>/i.test(String(content).trim()))
  );
}

function normalizedPlainText(content, contentFormat) {
  if (!content) return '';
  if (isHtmlContent(content, contentFormat)) return extractPlainTextFromHtml(String(content)).trim();
  return String(content).replace(/\r\n/g, '\n').trim();
}

function safeFileBaseName(name) {
  const trimmed = (name || 'Document').trim() || 'Document';
  return trimmed.replace(/\s+/g, '_').replace(/[^\w.-]/g, '') || 'Document';
}

function buildPlainTextPdfBlob(fileName, safe, plain) {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });
  pdf.setFontSize(18);
  pdf.setTextColor(0, 0, 0);
  pdf.text(fileName.trim() || safe, 20, 20);
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.5);
  pdf.line(20, 24, 190, 24);
  pdf.setFontSize(12);
  const pageWidth = 170;
  const lineHeight = 7;
  let y = 30;
  plain.split('\n').forEach((paragraph) => {
    if (!paragraph.trim()) {
      y += lineHeight / 2;
      return;
    }
    if (y > 270) {
      pdf.addPage();
      y = 20;
    }
    const lines = pdf.splitTextToSize(paragraph, pageWidth);
    pdf.text(lines, 20, y);
    y += lines.length * lineHeight + 2;
  });
  return pdf.output('blob');
}

async function buildPlainTextDocxBlob(plain) {
  const lines = plain.split('\n');
  const paragraphs = lines.map(
    (line) =>
      new Paragraph({
        children: [new TextRun(line.length ? line : ' ')],
      }),
  );
  const doc = new Document({
    sections: [
      {
        children: paragraphs.length ? paragraphs : [new Paragraph({ children: [new TextRun(' ')] })],
      },
    ],
  });
  return Packer.toBlob(doc);
}

/**
 * @param {string} fileName
 * @param {{ value: string, label: string, mimeType: string }} fileType
 * @param {string} content — raw document string (HTML or plain)
 * @param {'html'|'text'|'auto'} contentFormat
 * @returns {Promise<File>}
 *
 * Cuando el contenido es HTML (la salida real del editor del chat), .docx y
 * .pdf se construyen con las mismas rutinas HTML-aware que usa "Download"
 * (`htmlToDocxSectionChildren`, `renderHtmlToPdf`) — preservan tablas,
 * encabezados y listas — en vez de aplanar todo a texto plano primero como
 * hacía este archivo antes (CT-5305: era el flujo con más pérdida de
 * formato de los dos). El texto plano queda solo como fallback: contenido
 * que ya es texto/markdown, o si el render HTML llega a fallar.
 */
export async function buildDriveSaveFile(fileName, fileType, content, contentFormat) {
  const source = typeof content === 'string' ? content : String(content ?? '');
  if (!source.trim()) {
    throw new Error('Document is empty');
  }
  const isHtml = isHtmlContent(source, contentFormat);

  const safe = safeFileBaseName(fileName);
  const type = fileType?.value || 'txt';
  const mimeType = fileType?.mimeType || 'text/plain';

  const plain = () => normalizedPlainText(source, contentFormat);
  const requirePlain = () => {
    const text = plain();
    if (!text) throw new Error('Document is empty');
    return text;
  };

  switch (type) {
    case 'txt': {
      const blob = new Blob([requirePlain()], { type: mimeType });
      return new File([blob], `${safe}.txt`, { type: mimeType });
    }
    case 'pdf': {
      if (isHtml) {
        try {
          const pdf = await renderHtmlToPdf(source);
          return new File([pdf.output('blob')], `${safe}.pdf`, { type: mimeType });
        } catch {
          // El HTML no se pudo renderizar (fuentes, contenido vacío tras
          // parseo, etc.) — cae al PDF de texto plano en vez de fallar.
        }
      }
      const blob = buildPlainTextPdfBlob(fileName, safe, requirePlain());
      return new File([blob], `${safe}.pdf`, { type: mimeType });
    }
    case 'docx': {
      if (isHtml) {
        const children = htmlToDocxSectionChildren(source, {
          Paragraph,
          TextRun,
          Table,
          TableRow,
          TableCell,
        });
        const doc = new Document({ sections: [{ children }] });
        const blob = await Packer.toBlob(doc);
        return new File([blob], `${safe}.docx`, { type: mimeType });
      }
      const blob = await buildPlainTextDocxBlob(requirePlain());
      return new File([blob], `${safe}.docx`, { type: mimeType });
    }
    default: {
      const blob = new Blob([requirePlain()], { type: 'text/plain' });
      return new File([blob], `${safe}.txt`, { type: 'text/plain' });
    }
  }
}

function unwrapResponse(res) {
  return res?.data ?? res ?? {};
}

/**
 * @param {import('axios').AxiosInstance} apiClient
 * @param {File} file
 * @param {{ vaultUuid: string, folderUuid: string|null, vaultName?: string, folderName?: string }} destination
 * @param {{ userId?: string|null, tenantId?: string|null }} ctx
 */
export async function uploadDriveFileToDestination(apiClient, file, destination, ctx) {
  const { vaultUuid, folderUuid } = destination;
  const { userId, tenantId } = ctx;

  if (!vaultUuid) {
    throw new Error('Vault is required');
  }

  const createDocumentBody = {
    name: file.name,
    vaultUuid,
    folderUuid: folderUuid || null,
    type: file.type || 'application/octet-stream',
    url: '',
    metadata: {
      source: 'chat_document_panel',
      version: '1.0',
      region: 'us-east-1',
      confidential: true,
    },
  };

  const createRes = await createDocumentByTenant(apiClient, createDocumentBody, {
    timeout: DRIVE_SAVE_API_TIMEOUT_MS,
  });
  const data = unwrapResponse(createRes);
  const statusCode = data.status_code ?? data.statusCode;
  if (statusCode != null && (statusCode < 200 || statusCode >= 300)) {
    throw new Error(data.msgCode?.[0]?.description || 'Failed to create document');
  }
  const body = data.body ?? data;
  const documentUuid =
    body?.document?.document_uuid ?? body?.document?.documentUuid ?? body?.document_uuid;
  const versionUuid =
    body?.version?.doc_version_uuid ??
    body?.version?.docVersionUuid ??
    body?.version_uuid;

  if (!documentUuid || !versionUuid) {
    throw new Error('Invalid document creation response');
  }

  const createFileBody = {
    name: file.name,
    folder_id: folderUuid ? String(folderUuid) : '-',
    tenant_id: tenantId || '',
    user_id: userId || '',
    file_type: file.type || 'application/octet-stream',
    status: 'active',
    vault_uuid: vaultUuid,
    document_uuid: documentUuid,
    version_uuid: versionUuid,
  };

  const fileRes = await createTenantFile(apiClient, createFileBody, {
    timeout: DRIVE_SAVE_API_TIMEOUT_MS,
  });
  const fileData = unwrapResponse(fileRes);
  const inner = fileData?.body ?? fileData;
  const uploadUrl = inner?.uploadUrl ?? inner?.upload_url;
  const fileUuid =
    inner?.file?.uuid ??
    inner?.uuid ??
    inner?.file_uuid ??
    inner?.fileUuid;

  if (!uploadUrl || !fileUuid) {
    throw new Error('No upload URL or file id returned');
  }

  const putResponse = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  if (!putResponse.ok) {
    throw new Error(`Upload failed: ${putResponse.status}`);
  }

  await attachDocumentByTenant(
    apiClient,
    {
      file_uuid: fileUuid,
      document_uuid: documentUuid,
    },
    { timeout: DRIVE_SAVE_API_TIMEOUT_MS },
  );

  return {
    vaultUuid,
    vaultName: destination.vaultName,
    folderName: destination.folderName || 'Root',
    documentUuid,
  };
}

/**
 * @param {import('axios').AxiosInstance} apiClient
 * @param {File} file
 * @param {Array<{ vaultUuid: string, folderUuid: string|null, vaultName?: string, folderName?: string }>} destinations
 * @param {{ userId?: string|null, tenantId?: string|null }} ctx
 * @returns {Promise<{ successCount: number, errors: string[], firstResult?: object }>}
 */
export async function saveDriveFileToDestinations(apiClient, file, destinations, ctx) {
  let successCount = 0;
  const errors = [];
  let firstResult = null;

  for (const dest of destinations) {
    try {
      const result = await uploadDriveFileToDestination(apiClient, file, dest, ctx);
      successCount += 1;
      if (!firstResult) firstResult = result;
    } catch (e) {
      const label = `${dest.vaultName || 'Vault'}/${dest.folderName || 'Root'}`;
      errors.push(`${label}: ${e?.message || 'Unknown error'}`);
    }
  }

  return { successCount, errors, firstResult };
}
